import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

import { vestResolver } from "@hookform/resolvers/vest";
import { merge } from "lodash";

import { useAppDispatch } from "app/hooks";
import {
  setActionType,
  setCustomMessage,
  setLabel,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import CPTCodeField from "components/cpt-code-field";
import CPTCodesMultiField from "components/cpt-code-field/cpt-codes-multi-field.component";
import InputFormField from "components/input-form-field";
import ManufacturerField from "components/manufacturer-field";
import ModalForm from "components/modal-form";
import SelectFormField from "components/select-form-field";
import { MULTIPLE_CPT_CODES_FEATURE } from "constants/feature-flags";
import {
  ACCESSORIES_SUB_TYPES,
  FEE_SUB_TYPES,
  SERVICES_SUB_TYPES
} from "constants/products";
import {
  ACCESSORY_PRODUCT_TYPE,
  COCHLEAR_IMPLANT_PRODUCT_TYPE,
  FEE_PRODUCT_TYPE,
  HEARING_AID_PRODUCT_TYPE,
  PRODUCT_TYPES,
  SERVICE_PRODUCT_TYPE,
  SUPPLY_PRODUCT_TYPE
} from "constants/sales";
import { CPTCode } from "services/cpt-codes";
import { useAddMasterProductMutation } from "services/master-products";
import { Manufacturer } from "services/types/manufacturers.type";
import { MasterProduct } from "services/types/master-products.type";
import { Product } from "services/types/products.type";

import { formatToMasterProductInput } from "./add-master-product-form.utils";
import { formValidator } from "./form-validator";
import SelectProductFamily from "./select-product-family.component";
import SpecificationForm from "./specification-form";

type AddMasterProductFormProps = {
  isOpen: boolean;
  onCancel: () => void;
  onSave: (product: MasterProduct) => void;
  newProduct?: Partial<AddMasterProductFormValues>;
  disableTypeSelection?: boolean;
  disableSubTypeSelection?: boolean;
};

export type AddMasterProductFormSpecificationValues = {
  technology: "";
  type: "";
  allowed_subtypes: number[];
  warranty_months: "";
  warranty_field: "";
  hearing_aids: Product[];
  color: "";
  size: "";
};

export type AddMasterProductFormValues = {
  type: string;
  subtype: string;
  manufacturer: Manufacturer | null;
  model: string;
  description: string;
  cpt_code: CPTCode | null;
  cpt_codes: CPTCode[];
  product_family: string | null;
  specification: AddMasterProductFormSpecificationValues | null;
};

const DEFAULT_SPECIFICATION: AddMasterProductFormSpecificationValues = {
  technology: "",
  type: "",
  allowed_subtypes: [],
  warranty_months: "",
  warranty_field: "",
  hearing_aids: [],
  color: "",
  size: ""
};

const DEFAULT_VALUES: AddMasterProductFormValues = {
  type: "",
  subtype: "",
  manufacturer: null,
  model: "",
  description: "",
  cpt_code: null,
  cpt_codes: [],
  product_family: null,
  specification: DEFAULT_SPECIFICATION
};

export const getSubTypeOptions = (
  type: number
): { value: string; label: string }[] => {
  if (type === ACCESSORY_PRODUCT_TYPE) {
    return ACCESSORIES_SUB_TYPES.getOptions();
  } else if (type === SERVICE_PRODUCT_TYPE) {
    return SERVICES_SUB_TYPES.getOptions();
  } else if (type === FEE_PRODUCT_TYPE) {
    return FEE_SUB_TYPES.getOptions();
  }

  return [];
};

export default function AddMasterProductForm({
  newProduct,
  isOpen,
  disableTypeSelection,
  disableSubTypeSelection,
  onCancel,
  onSave
}: AddMasterProductFormProps) {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const multipleCptCodesFeature = Boolean(flags[MULTIPLE_CPT_CODES_FEATURE]);
  const defaultValues: AddMasterProductFormValues = useMemo(() => {
    if (!isOpen) return DEFAULT_VALUES;
    return merge({}, DEFAULT_VALUES, newProduct);
  }, [isOpen, newProduct]);

  const formMethods = useForm<AddMasterProductFormValues>({
    mode: "onChange",
    resolver: vestResolver(formValidator(), undefined, {
      mode: "sync"
    }),
    defaultValues
  });

  const { control, reset, handleSubmit, watch, clearErrors, setValue } =
    formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const [type, manufacturer, subtype, cpt_code] = watch<
    ["type", "manufacturer", "subtype", "cpt_code"]
  >(["type", "manufacturer", "subtype", "cpt_code"]);

  const isFeeProductType = Number(type) === FEE_PRODUCT_TYPE;
  const isServiceProductType = Number(type) === SERVICE_PRODUCT_TYPE;

  useEffect(() => {
    clearErrors([
      "subtype",
      "specification.technology",
      "specification.color",
      "description",
      "manufacturer"
    ]);
  }, [type, clearErrors]);

  useEffect(() => {
    clearErrors([
      "specification.type",
      "specification.warranty_field",
      "specification.warranty_months",
      "specification.color"
    ]);
  }, [subtype, clearErrors]);

  useEffect(() => {
    if (
      [
        HEARING_AID_PRODUCT_TYPE,
        SUPPLY_PRODUCT_TYPE,
        FEE_PRODUCT_TYPE,
        COCHLEAR_IMPLANT_PRODUCT_TYPE
      ].includes(Number(type))
    ) {
      setValue("subtype", "");
    }
    if (type) {
      setValue("specification", structuredClone(DEFAULT_SPECIFICATION));
    }
  }, [type, setValue, isFeeProductType]);

  const [addMasterProduct, { isLoading: isSubmitting }] =
    useAddMasterProductMutation();

  const onSubmit = useCallback(
    (data: AddMasterProductFormValues) => {
      const request = formatToMasterProductInput(data, multipleCptCodesFeature);

      addMasterProduct(request).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(
              "An Error has Occurred, please reach out to support."
            )
          );
          dispatch(setOpen(true));
        } else {
          dispatch(setLabel("Product"));
          dispatch(setCustomMessage("Product added"));
          dispatch(setActionType("add"));
          dispatch(setOpen(true));
          onSave(response.data);
        }
      });
    },
    [addMasterProduct, dispatch, onSave, multipleCptCodesFeature]
  );

  const subtypeOptions = getSubTypeOptions(Number(type));

  return (
    <ModalForm
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onCancel={() => {
        reset();
        onCancel();
      }}
      onSubmit={handleSubmit(onSubmit)}
      title="New Product Quick Add"
      hideBackdrop={false}
      formName="product"
      control={control}
      maxWidth="lg"
      zIndex={1502}
    >
      <Stack sx={{ mt: 0, pb: 2 }}>
        <Grid container width="100%" sx={{ pb: 4 }} columnSpacing={4}>
          <Grid item xs={6}>
            <SelectFormField
              name="type"
              label="Type"
              control={control}
              options={PRODUCT_TYPES.getOptions()}
              required
              dropDownZIndex={1502}
              disabled={disableTypeSelection}
            />
          </Grid>
          <Grid item xs={6}>
            {Boolean(type) &&
              !isFeeProductType &&
              Boolean(subtypeOptions?.length) && (
                <SelectFormField
                  name="subtype"
                  label="Sub Type"
                  control={control}
                  options={subtypeOptions}
                  required={!isFeeProductType}
                  disabled={disableSubTypeSelection}
                  dropDownZIndex={1502}
                />
              )}
          </Grid>
        </Grid>

        {!isFeeProductType && (
          <Grid container width="100%" sx={{ pb: 4 }} columnSpacing={4}>
            <Grid item xs={12}>
              <CPTCodeField name="cpt_code" control={control} sx={{ ml: 0 }} />
            </Grid>
            {multipleCptCodesFeature && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <CPTCodesMultiField
                  name="cpt_codes"
                  label="Additional CPT or HCPCS Codes"
                  control={control}
                  excludeIds={cpt_code?.id ? [cpt_code.id] : undefined}
                />
              </Grid>
            )}
          </Grid>
        )}

        {!isFeeProductType && !isServiceProductType && (
          <Grid container width="100%" sx={{ pb: 4 }} columnSpacing={4}>
            <Grid item xs={6}>
              <ManufacturerField
                required={!isFeeProductType}
                name="manufacturer"
                control={control}
                practiceFiltered
                sx={{ mt: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <SelectProductFamily
                manufacturer={manufacturer?.id}
                control={control}
                name="product_family"
                disabled={!manufacturer?.id}
              />
            </Grid>
          </Grid>
        )}

        <Grid container width="100%" sx={{ pb: 4 }} columnSpacing={4}>
          {!isFeeProductType && !isServiceProductType && (
            <Grid item xs={6}>
              <InputFormField
                name="model"
                label="Model"
                autoComplete="off"
                control={control}
                sx={{ ml: 0 }}
                required={!isFeeProductType && !isServiceProductType}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>
          )}
          <Grid item xs={isServiceProductType ? 12 : 6}>
            <InputFormField
              name="description"
              label="Description"
              control={control}
              sx={{ ml: 0 }}
              required={isServiceProductType || isFeeProductType}
            />
          </Grid>
        </Grid>
        <FormProvider {...formMethods}>
          <SpecificationForm />
        </FormProvider>
      </Stack>
    </ModalForm>
  );
}
